import { useConvexMutation } from '@convex-dev/react-query'
import { api } from '@cvx/_generated/api'
import type { Id } from '@cvx/_generated/dataModel'
import type { AdditionalInfoField } from '@cvx/features/jobs/additional_info_fields_db'
import type { DragEndEvent } from '@dnd-kit/core'
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Button } from '@rekrytering/ui/components/button'
import { Input } from '@rekrytering/ui/components/input'
import { cn } from '@rekrytering/ui/lib/utils'
import { useMutation } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

type FieldType = 'text' | 'number' | 'date' | 'url' | 'email'

type AdditionalInfoFieldDraft = {
  readonly id: string
  label: string
  placeholder: string
  type: FieldType
  required: boolean
}

const createEmptyField = (): AdditionalInfoFieldDraft => ({
  id: crypto.randomUUID(),
  label: '',
  placeholder: '',
  type: 'text',
  required: false,
})

const fromFields = (
  fields: readonly AdditionalInfoField[],
): AdditionalInfoFieldDraft[] =>
  fields.map((f) => ({
    id: f._id,
    label: f.label,
    placeholder: f.placeholder,
    type: f.type,
    required: f.required,
  }))

type SortableFieldProps = {
  readonly field: AdditionalInfoFieldDraft
  readonly index: number
  readonly isLocked: boolean
  readonly onUpdate: (
    id: string,
    patch: Partial<AdditionalInfoFieldDraft>,
  ) => void
  readonly onRemove: (id: string) => void
}

const SortableField = ({
  field,
  index,
  isLocked,
  onUpdate,
  onRemove,
}: SortableFieldProps) => {
  const { t } = useTranslation()
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: field.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'rounded-lg border border-border bg-background p-4',
        isDragging && 'opacity-50',
      )}
    >
      <div className="flex gap-3">
        <button
          type="button"
          className={cn(
            'mt-2 touch-none text-primary/30',
            isLocked
              ? 'cursor-not-allowed'
              : 'cursor-grab hover:text-primary/60 active:cursor-grabbing',
          )}
          {...attributes}
          {...(isLocked ? {} : listeners)}
          aria-label={t('jobs.additionalInfo.drag')}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="currentColor"
          >
            <circle cx="9" cy="6" r="1.5" />
            <circle cx="15" cy="6" r="1.5" />
            <circle cx="9" cy="12" r="1.5" />
            <circle cx="15" cy="12" r="1.5" />
            <circle cx="9" cy="18" r="1.5" />
            <circle cx="15" cy="18" r="1.5" />
          </svg>
        </button>

        <div className="flex flex-1 flex-col gap-3">
          <div className="flex items-center gap-2">
            <span className="shrink-0 text-xs font-medium text-primary/40">
              {index + 1}.
            </span>
            <Input
              value={field.label}
              onChange={(e) => onUpdate(field.id, { label: e.target.value })}
              placeholder={t('jobs.additionalInfo.labelPlaceholder')}
              className="flex-1"
              disabled={isLocked}
            />
            <select
              value={field.type}
              onChange={(e) =>
                onUpdate(field.id, { type: e.target.value as FieldType })
              }
              className="h-9 rounded-md border border-input bg-background px-3 text-sm disabled:cursor-not-allowed disabled:opacity-50"
              disabled={isLocked}
            >
              <option value="text">{t('jobs.additionalInfo.typeText')}</option>
              <option value="number">
                {t('jobs.additionalInfo.typeNumber')}
              </option>
              <option value="date">{t('jobs.additionalInfo.typeDate')}</option>
              <option value="url">{t('jobs.additionalInfo.typeUrl')}</option>
              <option value="email">
                {t('jobs.additionalInfo.typeEmail')}
              </option>
            </select>
          </div>

          <div className="flex items-center gap-2 pl-5">
            <Input
              value={field.placeholder}
              onChange={(e) =>
                onUpdate(field.id, { placeholder: e.target.value })
              }
              placeholder={t('jobs.additionalInfo.placeholderPlaceholder')}
              className="flex-1"
              disabled={isLocked}
            />
            <label
              className={cn(
                'flex shrink-0 items-center gap-1.5 text-sm text-primary/60',
                isLocked ? 'cursor-not-allowed' : 'cursor-pointer',
              )}
            >
              <input
                type="checkbox"
                checked={field.required}
                onChange={(e) =>
                  onUpdate(field.id, { required: e.target.checked })
                }
                className="size-4 cursor-pointer disabled:cursor-not-allowed"
                disabled={isLocked}
              />
              {t('jobs.additionalInfo.requiredLabel')}
            </label>
          </div>

          {!isLocked && (
            <button
              type="button"
              onClick={() => onRemove(field.id)}
              className="self-start text-xs text-destructive/70 hover:text-destructive"
            >
              {t('jobs.additionalInfo.remove')}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

type AdditionalInfoFieldsEditorProps = {
  readonly jobId: Id<'jobs'>
  readonly initialFields: readonly AdditionalInfoField[]
  readonly isLocked: boolean
}

export const AdditionalInfoFieldsEditor = ({
  jobId,
  initialFields,
  isLocked,
}: AdditionalInfoFieldsEditorProps) => {
  const { t } = useTranslation()
  const [fields, setFields] = useState<AdditionalInfoFieldDraft[]>(() =>
    fromFields(initialFields),
  )
  const [saved, setSaved] = useState(false)

  const { mutateAsync: saveFields, isPending } = useMutation({
    mutationFn: useConvexMutation(
      api.features.jobs.jobs.upsertMyAdditionalInfoFields,
    ),
  })

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  const handleDragEnd = (event: DragEndEvent): void => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    setFields((prev) => {
      const oldIndex = prev.findIndex((f) => f.id === active.id)
      const newIndex = prev.findIndex((f) => f.id === over.id)
      return arrayMove(prev, oldIndex, newIndex)
    })
  }

  const handleUpdate = (
    id: string,
    patch: Partial<AdditionalInfoFieldDraft>,
  ): void => {
    setFields((prev) => prev.map((f) => (f.id === id ? { ...f, ...patch } : f)))
  }

  const handleRemove = (id: string): void => {
    setFields((prev) => prev.filter((f) => f.id !== id))
  }

  const handleSave = async (): Promise<void> => {
    await saveFields({
      jobId,
      fields: fields.map((f, i) => ({
        label: f.label,
        placeholder: f.placeholder,
        type: f.type,
        required: f.required,
        order: i,
      })),
    })
    setSaved(true)
    setTimeout(() => setSaved(false), 2000)
  }

  return (
    <div className="flex flex-col gap-4">
      {isLocked && (
        <div className="flex items-center gap-2 rounded-md border border-orange-200 bg-orange-50 px-4 py-3 text-sm text-orange-800 dark:border-orange-800/40 dark:bg-orange-900/20 dark:text-orange-400">
          <span>🔒</span>
          <span>{t('jobs.locked.notice')}</span>
        </div>
      )}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={fields.map((f) => f.id)}
          strategy={verticalListSortingStrategy}
        >
          {fields.map((field, index) => (
            <SortableField
              key={field.id}
              field={field}
              index={index}
              isLocked={isLocked}
              onUpdate={handleUpdate}
              onRemove={handleRemove}
            />
          ))}
        </SortableContext>
      </DndContext>

      {fields.length === 0 && (
        <p className="py-8 text-center text-sm text-primary/40">
          {t('jobs.additionalInfo.empty')}
        </p>
      )}

      {!isLocked && (
        <div className="flex items-center gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => setFields((prev) => [...prev, createEmptyField()])}
          >
            {t('jobs.additionalInfo.addField')}
          </Button>
          <Button type="button" onClick={handleSave} disabled={isPending}>
            {isPending
              ? t('jobs.additionalInfo.saving')
              : t('jobs.additionalInfo.save')}
          </Button>
          {saved && (
            <span className="text-sm text-primary/60">
              {t('jobs.additionalInfo.saved')}
            </span>
          )}
        </div>
      )}
    </div>
  )
}
